import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import { Archive, ArchiveRestore, Heart, Star, Trash2, Upload, X } from "lucide-react";
import { ACCESS_TIERS, VIDEO_TAG_DIMENSIONS, type VideoTagDimension } from "@demore/shared/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, ApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { YoutubeEmbed } from "../viewer/YoutubeEmbed";
import { useRef, useState } from "react";
import { useAuth } from "@/lib/auth";

type DetailResponse = {
  video: {
    id: string;
    title: string;
    description: string | null;
    category: string;
    accessTier: string;
    status: string;
    excludedFromNewPlaylists: boolean;
    currentVersionId: string | null;
    reviewDueDate: string | null;
    publishedAt: string | null;
  };
  versions: Array<{ id: string; versionNumber: number; hasPlayback: boolean; createdAt: string; note: string | null; source?: "mux" | "youtube"; youtubeVideoId?: string | null }>;
  tags: Array<{ id: string; dimension: VideoTagDimension; value: string }>;
  approvals: Array<{ id: string; action: string; comment: string | null; createdAt: string; actorName: string | null }>;
  attachments: Array<{ id: string; filename: string; r2Key: string; accessTier: string }>;
  permissions: { canEdit: boolean };
};

export function VideoDetailPage() {
  const { videoId } = useParams<{ videoId: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const [rejectComment, setRejectComment] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data } = useQuery({
    queryKey: ["video", videoId],
    queryFn: () => api.get<DetailResponse>(`/api/admin/videos/${videoId}`),
    enabled: !!videoId,
  });

  const submit = useMutation({
    mutationFn: () => api.post(`/api/admin/videos/${videoId}/submit`, {}),
    onSuccess: () => { toast.success("Submitted for approval"); qc.invalidateQueries({ queryKey: ["video", videoId] }); },
  });
  const approve = useMutation({
    mutationFn: () => api.post(`/api/admin/videos/${videoId}/approve`, {}),
    onSuccess: () => { toast.success("Approved"); qc.invalidateQueries({ queryKey: ["video", videoId] }); },
  });
  const unapprove = useMutation({
    mutationFn: () => api.post(`/api/admin/videos/${videoId}/unapprove`, {}),
    onSuccess: () => {
      toast.success("Unapproved — returned to draft");
      qc.invalidateQueries({ queryKey: ["video", videoId] });
      qc.invalidateQueries({ queryKey: ["library"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Unapprove failed"),
  });
  const reject = useMutation({
    mutationFn: () => api.post(`/api/admin/videos/${videoId}/reject`, { comment: rejectComment }),
    onSuccess: () => { toast.success("Rejected"); setRejectComment(""); qc.invalidateQueries({ queryKey: ["video", videoId] }); },
  });
  const archive = useMutation({
    mutationFn: () => api.post(`/api/admin/videos/${videoId}/archive`, {}),
    onSuccess: () => {
      toast.success("Archived — hidden from new playlists");
      qc.invalidateQueries({ queryKey: ["video", videoId] });
      qc.invalidateQueries({ queryKey: ["library"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Archive failed"),
  });
  const unarchive = useMutation({
    mutationFn: () => api.post<{ status: string }>(`/api/admin/videos/${videoId}/unarchive`, {}),
    onSuccess: (res) => {
      toast.success(
        res?.status === "draft"
          ? "Restored as draft — submit it for approval to use again"
          : "Restored to approved",
      );
      qc.invalidateQueries({ queryKey: ["video", videoId] });
      qc.invalidateQueries({ queryKey: ["library"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Restore failed"),
  });
  const markCurrent = useMutation({
    mutationFn: () => api.post(`/api/admin/videos/${videoId}/mark-current`, {}),
    onSuccess: () => {
      toast.success("Marked current");
      qc.invalidateQueries({ queryKey: ["video", videoId] });
      qc.invalidateQueries({ queryKey: ["library"] });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not mark current"),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/admin/videos/${videoId}`),
    onSuccess: () => {
      toast.success("Video deleted");
      qc.invalidateQueries({ queryKey: ["library"] });
      nav("/admin/library");
    },
    onError: (err) => {
      setConfirmDelete(false);
      if (err instanceof ApiError && err.status === 409) {
        toast.error(err.message, { duration: 8000 });
      } else {
        toast.error(err instanceof Error ? err.message : "Delete failed");
      }
    },
  });

  // Favorites — per-user; we read from the my-favorites list rather than
  // bloating the detail response.
  const { data: favs } = useQuery({
    queryKey: ["my-favorites"],
    queryFn: () => api.get<{ items: Array<{ id: string }> }>("/api/admin/videos?favoritesOnly=true"),
  });
  const isFavorite = !!favs?.items.find((v) => v.id === videoId);
  const toggleFavorite = useMutation({
    mutationFn: () =>
      isFavorite
        ? api.delete(`/api/admin/videos/${videoId}/favorite`)
        : api.post(`/api/admin/videos/${videoId}/favorite`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-favorites"] });
      // Bug 6: the playlist builder's Favorites tab reads ["library-tabbed"].
      qc.invalidateQueries({ queryKey: ["library-tabbed"] });
    },
  });

  // Promoted (super_admin)
  const { data: promotedList } = useQuery({
    queryKey: ["promoted-list"],
    queryFn: () => api.get<{ items: Array<{ videoId: string; active: boolean }> }>("/api/admin/settings/promoted"),
    enabled: user?.role === "super_admin",
  });
  const isPromoted = !!promotedList?.items.find((p) => p.videoId === videoId && p.active);
  const togglePromoted = useMutation({
    mutationFn: () =>
      isPromoted
        ? api.delete(`/api/admin/videos/${videoId}/promote`)
        : api.post(`/api/admin/videos/${videoId}/promote`, {}),
    onSuccess: () => {
      toast.success(isPromoted ? "Removed from promoted" : "Promoted — surfaces first in builder");
      qc.invalidateQueries({ queryKey: ["promoted-list"] });
      // Bug 2: Settings → Promoted tab uses ["settings-promoted"]; the builder
      // promoted tab uses ["library-tabbed"]. Refresh both.
      qc.invalidateQueries({ queryKey: ["settings-promoted"] });
      qc.invalidateQueries({ queryKey: ["library-tabbed"] });
    },
  });

  const saveTags = useMutation({
    mutationFn: (tags: Array<{ dimension: VideoTagDimension; value: string }>) =>
      api.put(`/api/admin/videos/${videoId}/tags`, { tags }),
    onSuccess: () => { toast.success("Tags saved"); qc.invalidateQueries({ queryKey: ["video", videoId] }); },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Save failed"),
  });

  // Bug 11: a YouTube-source video never has Mux playback, so the Mux
  // "transcoding…" poll would spin forever. Detect the source up front and
  // render the YouTube embed instead.
  const currentVer =
    data?.versions.find((ver) => ver.id === data.video.currentVersionId) ??
    data?.versions[0];
  const youtubeId =
    currentVer?.source === "youtube" ? currentVer.youtubeVideoId ?? null : null;

  // Admin-side preview — signs tokens for whichever version is ready.
  const { data: playback } = useQuery({
    queryKey: ["admin-playback", videoId],
    queryFn: () =>
      api.post<{ playbackUrl: string; versionNumber: number }>(
        `/api/admin/videos/${videoId}/playback`,
      ),
    enabled: !!videoId && !!data && !youtubeId,
    retry: false,
    refetchInterval: (q) => (q.state.data ? false : 10_000), // poll while transcoding
  });

  // "Upload new version" — mints a Mux direct-upload URL for this video,
  // opens a file picker, PUTs the file, then auto-submits for approval.
  const newVersionRef = useRef<HTMLInputElement | null>(null);
  const uploadVersion = useMutation({
    mutationFn: async () => {
      newVersionRef.current?.click();
    },
  });
  async function handleNewVersionFile(file: File) {
    try {
      const upload = await api.post<{ uploadUrl: string; uploadId: string; versionId: string }>(
        "/api/admin/videos/mux-upload",
        { videoId, note: "Quarterly review refresh" },
      );
      if (!upload.uploadUrl.startsWith("data:")) {
        const put = await fetch(upload.uploadUrl, { method: "PUT", body: file });
        if (!put.ok) throw new Error(`HTTP ${put.status}`);
      }
      await api.post(`/api/admin/videos/${videoId}/submit`, { note: "New version (quarterly review)" });
      toast.success("New version uploaded — submitted for approval");
      qc.invalidateQueries({ queryKey: ["video", videoId] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    }
  }

  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const v = data.video;
  // Server-computed capability (same can() rule the mutation routes enforce) —
  // media actions follow it so the UI matches actual authorization (#2504-2506
  // follow-up; ported from Codex's access-rights fix).
  const canEdit = data.permissions.canEdit;
  const canApprove = user?.role === "super_admin";

  const isSuperAdmin = user?.role === "super_admin";
  const canArchive =
    isSuperAdmin || user?.role === "campaign_owner";

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="font-head text-2xl font-semibold">{v.title}</h1>
        <Badge variant={v.status === "approved" ? "ok" : v.status === "pending_approval" ? "default" : v.status === "archived" ? "danger" : "muted"}>
          {v.status.replace(/_/g, " ")}
        </Badge>
        {v.excludedFromNewPlaylists && <Badge variant="danger">review overdue</Badge>}
        <div className="ml-auto flex gap-2 flex-wrap">
          {(v.status === "approved" || v.status === "rejected") && canEdit && (
            <Button variant="outline" size="sm" onClick={() => uploadVersion.mutate()} disabled={uploadVersion.isPending} title="Upload a new version">
              <Upload className="h-4 w-4" /> New version
            </Button>
          )}
          <Button
            variant={isFavorite ? "primary" : "ghost"}
            size="sm"
            onClick={() => toggleFavorite.mutate()}
            disabled={toggleFavorite.isPending}
            title={isFavorite ? "Remove favorite" : "Mark favorite"}
          >
            <Heart className={`h-4 w-4 ${isFavorite ? "fill-current" : ""}`} />
            {isFavorite ? "Favorited" : "Favorite"}
          </Button>
          {isSuperAdmin && (
            <Button
              variant={isPromoted ? "primary" : "ghost"}
              size="sm"
              onClick={() => togglePromoted.mutate()}
              disabled={togglePromoted.isPending}
              title={isPromoted ? "Unpromote" : "Promote — surface first in builder"}
            >
              <Star className={`h-4 w-4 ${isPromoted ? "fill-current" : ""}`} />
              {isPromoted ? "Promoted" : "Promote"}
            </Button>
          )}
          {v.status === "archived" && canArchive && (
            <Button variant="outline" size="sm" onClick={() => unarchive.mutate()} disabled={unarchive.isPending}>
              <ArchiveRestore className="h-4 w-4" /> Restore
            </Button>
          )}
          {v.status !== "archived" && canArchive && (
            <Button variant="outline" size="sm" onClick={() => archive.mutate()} disabled={archive.isPending}>
              <Archive className="h-4 w-4" /> Archive
            </Button>
          )}
          {isSuperAdmin && (
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{v.description ?? "No description."}</p>

      <Card>
        <CardContent className="p-0">
          <div className="aspect-video bg-navy rounded-md overflow-hidden">
            {youtubeId ? (
              <YoutubeEmbed videoId={youtubeId} title={v.title} onProgress={() => {}} />
            ) : playback?.playbackUrl ? (
              <MuxPlayer
                src={playback.playbackUrl}
                streamType="on-demand"
                accentColor="#E6AC2A"
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white/70 gap-2 text-sm">
                <div className="animate-pulse">Transcoding…</div>
                <div className="text-xs">Mux is processing the upload. This usually takes 30s–2min.</div>
              </div>
            )}
          </div>
          {youtubeId ? (
            <div className="px-4 py-2 text-xs text-muted-foreground">
              YouTube video — plays via the IFrame embed; no Mux transcode or engagement milestones.
            </div>
          ) : playback?.versionNumber !== undefined && (
            <div className="px-4 py-2 text-xs text-muted-foreground">
              Previewing version {playback.versionNumber}{v.currentVersionId ? "" : " (not yet approved)"}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="md:col-span-2">
          <CardHeader><CardTitle>Approval</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {v.status === "draft" && canEdit && <Button onClick={() => submit.mutate()} disabled={submit.isPending}>Submit for approval</Button>}
              {v.status === "pending_approval" && canApprove && (
                <>
                  <Button onClick={() => approve.mutate()} disabled={approve.isPending}>Approve</Button>
                  <Button variant="destructive" onClick={() => reject.mutate()} disabled={!rejectComment || reject.isPending}>Reject</Button>
                </>
              )}
              {v.status === "approved" && canApprove && (
                <Button variant="outline" onClick={() => unapprove.mutate()} disabled={unapprove.isPending}>Unapprove</Button>
              )}
            </div>
            {v.status === "pending_approval" && canApprove && (
              <Textarea placeholder="Reason for rejection (required to reject)" value={rejectComment} onChange={(e) => setRejectComment(e.target.value)} />
            )}
            <ul className="text-sm divide-y divide-admin-rule">
              {data.approvals.length === 0 && (
                <li className="py-2 text-xs text-muted-foreground">
                  {v.status === "approved" && v.publishedAt
                    ? `Approved · ${formatDateTime(v.publishedAt)} (approver not recorded)`
                    : "No approval activity yet."}
                </li>
              )}
              {data.approvals.map((a) => (
                <li key={a.id} className="py-2">
                  <span className="font-medium capitalize">{a.action}</span>
                  {a.actorName && <span className="text-xs text-muted-foreground ml-2">by {a.actorName}</span>}
                  <span className="text-xs text-muted-foreground ml-2">{formatDateTime(a.createdAt)}</span>
                  {a.comment && <div className="text-xs">{a.comment}</div>}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Versions</CardTitle></CardHeader>
          <CardContent>
            <ul className="text-sm space-y-2">
              {data.versions.map((ver) => (
                <li key={ver.id} className="flex items-center justify-between border border-admin-rule rounded-md p-2">
                  <span>v{ver.versionNumber}</span>
                  <span className="text-xs text-muted-foreground">{formatDateTime(ver.createdAt)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <TagsEditor
        currentTags={data.tags.map((t) => ({ dimension: t.dimension, value: t.value }))}
        onSave={(tags) => saveTags.mutate(tags)}
        saving={saveTags.isPending}
        canEdit={canEdit}
      />

      <AttachmentsCard videoId={data.video.id} attachments={data.attachments} canEdit={data.permissions.canEdit} onChanged={() => qc.invalidateQueries({ queryKey: ["video", videoId] })} />

      {data.video.reviewDueDate && new Date(data.video.reviewDueDate).getTime() < Date.now() && v.status !== "archived" && (
        <Card>
          <CardContent className="flex items-center justify-between py-3 text-sm">
            <span className="text-muted-foreground">
              ⏰ Review due since {formatDateTime(data.video.reviewDueDate)} — upload a new version or archive.
            </span>
            <div className="flex gap-2">
              {canArchive && (
                <Button size="sm" variant="outline" onClick={() => markCurrent.mutate()} disabled={markCurrent.isPending}>
                  {markCurrent.isPending ? "Saving..." : "Mark current"}
                </Button>
              )}
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => uploadVersion.mutate()} disabled={uploadVersion.isPending}>
                  <Upload className="h-4 w-4" /> {uploadVersion.isPending ? "Starting…" : "Upload new version"}
                </Button>
              )}
              {canArchive && (
                <Button size="sm" variant="ghost" onClick={() => archive.mutate()} disabled={archive.isPending}>
                  <Archive className="h-4 w-4" /> Archive
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <input
        type="file"
        accept="video/*"
        ref={newVersionRef}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleNewVersionFile(f); }}
      />

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete "{v.title}"?</DialogTitle>
            <DialogDescription>
              This permanently removes the video, all its versions, tags, and approval history.
              <strong className="block mt-2 text-foreground">
                If the video has been added to any playlist or sent in a Demo Pack, deletion will be blocked —
                use Archive instead.
              </strong>
            </DialogDescription>
          </DialogHeader>
          <DialogBody />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => remove.mutate()} disabled={remove.isPending}>
              {remove.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Tags editor ────────────────────────────────────────────────────────
function TagsEditor({
  currentTags,
  onSave,
  saving,
  canEdit,
}: {
  currentTags: Array<{ dimension: VideoTagDimension; value: string }>;
  onSave: (tags: Array<{ dimension: VideoTagDimension; value: string }>) => void;
  saving: boolean;
  canEdit: boolean;
}) {
  const [tags, setTags] = useState(currentTags);
  const [dim, setDim] = useState<VideoTagDimension>("audience");
  const [value, setValue] = useState("");

  function add() {
    const v = value.trim();
    if (!v) return;
    if (tags.find((t) => t.dimension === dim && t.value === v)) {
      setValue("");
      return;
    }
    setTags((t) => [...t, { dimension: dim, value: v }]);
    setValue("");
  }
  function remove(i: number) {
    setTags((t) => t.filter((_, idx) => idx !== i));
  }
  const dirty = JSON.stringify(tags) !== JSON.stringify(currentTags);

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Tags</CardTitle>
          {canEdit && (
            <Button size="sm" disabled={!dirty || saving} onClick={() => onSave(tags)}>
              {saving ? "Saving…" : "Save tags"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          {tags.map((t, i) => (
            <Badge key={i} variant="outline" className="gap-1 pr-1">
              <span className="text-[10px] text-muted-foreground uppercase">{t.dimension}:</span>
              {t.value}
              {canEdit && (
                <button onClick={() => remove(i)} className="ml-1 p-0.5 hover:bg-foreground/10 rounded">
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          ))}
          {tags.length === 0 && <span className="text-xs text-muted-foreground">No tags yet</span>}
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Select value={dim} onChange={(e) => setDim(e.target.value as VideoTagDimension)} className="w-40">
              {VIDEO_TAG_DIMENSIONS.map((d) => <option key={d} value={d}>{d}</option>)}
            </Select>
            <Input
              placeholder="value (e.g. enterprise / cmo / fintech)"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            />
            <Button variant="outline" onClick={add}>Add</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Attachments ────────────────────────────────────────────────────────
function AttachmentsCard({
  videoId,
  attachments,
  canEdit,
  onChanged,
}: {
  videoId: string;
  attachments: Array<{ id: string; filename: string; r2Key: string; accessTier: string }>;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [tier, setTier] = useState("registered");
  const [pendingRemove, setPendingRemove] = useState<{ id: string; filename: string } | null>(null);
  const [removing, setRemoving] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  async function handleFile(file: File) {
    setUploading(true);
    try {
      const { uploadUrl } = await api.post<{ uploadUrl: string }>(
        `/api/admin/videos/${videoId}/attachments`,
        { filename: file.name, contentType: file.type || "application/octet-stream", sizeBytes: file.size, accessTier: tier },
      );
      // PUT directly to the signed URL.
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",       // required for Azure Blob
          "x-ms-blob-content-type": file.type || "application/octet-stream",
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });
      if (!put.ok) throw new Error(`Upload failed: HTTP ${put.status}`);
      toast.success(`Uploaded ${file.name}`);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function downloadAttachment(attId: string) {
    try {
      const { downloadUrl } = await api.get<{ downloadUrl: string }>(`/api/admin/videos/attachments/${attId}/download`);
      window.open(downloadUrl, "_blank");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Download failed");
    }
  }

  async function confirmRemove() {
    if (!pendingRemove) return;
    setRemoving(true);
    try {
      await api.delete(`/api/admin/videos/attachments/${pendingRemove.id}`);
      toast.success(`Removed ${pendingRemove.filename}`);
      setPendingRemove(null);
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Remove failed");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Attachments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {attachments.length === 0 && <p className="text-sm text-muted-foreground">No attachments.</p>}
        <ul className="text-sm space-y-1">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-center justify-between border border-admin-rule rounded-md p-2">
              <span>{a.filename}</span>
              <div className="flex items-center gap-2">
                <Badge variant="muted">{a.accessTier.replace(/_/g, " ")}</Badge>
                <Button size="sm" variant="ghost" onClick={() => downloadAttachment(a.id)}>Download</Button>
                {canEdit && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    aria-label={`Remove ${a.filename}`}
                    onClick={() => setPendingRemove({ id: a.id, filename: a.filename })}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
        {canEdit && (
          <div className="flex items-end gap-2 pt-2 border-t border-admin-rule">
            <div className="space-y-1.5 w-44">
              <Label>New attachment tier</Label>
              <Select value={tier} onChange={(e) => setTier(e.target.value)}>
                {ACCESS_TIERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </Select>
            </div>
            <input
              type="file"
              className="hidden"
              ref={fileRef}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            <Button variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()}>
              <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload file"}
            </Button>
          </div>
        )}
      </CardContent>
      <Dialog open={!!pendingRemove} onOpenChange={(o) => { if (!o && !removing) setPendingRemove(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove attachment?</DialogTitle>
            <DialogDescription>
              {pendingRemove
                ? `"${pendingRemove.filename}" will be permanently deleted. This can't be undone.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogBody />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPendingRemove(null)} disabled={removing}>Cancel</Button>
            <Button variant="destructive" onClick={confirmRemove} disabled={removing}>
              {removing ? "Removing…" : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
